import { useEffect, useMemo } from "react";
import { t } from "i18next";
import { useAuthStore } from "@/store/auth.store";
import { useUpdateUser } from "@/hooks/user/useUpdateUser";
import { toast } from "@/hooks/use-toast";
import { useTranscriptionLanguagesStore } from "@/store/transcriptionLanguages.store";
import { TranscriptionDefaultSelects } from "./components/TranscriptionDefaultSelects";
import type { TranscriptionSchemaType } from "@/schemas/transcription.schema";
import { FormProvider, useForm } from "react-hook-form";

const styles = [
  { value: "Bullet Point", label: "Bullet Point" },
  { value: "Paragraph", label: "Paragraph" },
];

const formats = [
  { value: "Detailed", label: "Detailed" },
  { value: "Concise", label: "Concise" },
];

const TranscriptionSettings = (): JSX.Element => {
  const { userData, getUser } = useAuthStore();
  const { updateUser, loading, result } = useUpdateUser();
  const {
    transcriptionLanguages,
    fetchTranscriptionLanguages,
    loading: loadingTranscriptionLanguages,
  } = useTranscriptionLanguagesStore();

  useEffect(() => {
    void fetchTranscriptionLanguages();
  }, [fetchTranscriptionLanguages]);

  const resetValues = useMemo(
    () => ({
      style: userData?.style ?? "",
      format: userData?.format ?? "",
      transcriptionLanguageId: userData?.userPreference.transcriptionLanguageId,
      transcriptionLanguage:
        transcriptionLanguages.find(
          (element) =>
            element.id === userData?.userPreference.transcriptionLanguageId
        )?.name ?? "",
    }),
    [userData, transcriptionLanguages]
  );

  const form = useForm<TranscriptionSchemaType>({
    shouldUnregister: false,
    defaultValues: resetValues,
  });

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.transcription.updatedToast.successful.title"),
          description: t(
            "settings.transcription.updatedToast.successful.description"
          ),
        });
        if (userData) void getUser(userData?.email);
      } else {
        toast({
          variant: "error",
          title: t("settings.transcription.updatedToast.error.title"),
          description: t(
            "settings.transcription.updatedToast.error.description"
          ),
        });
        form.reset(resetValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  const handleChange = (data: Partial<TranscriptionSchemaType>): void => {
    if (userData) {
      void updateUser({
        userId: userData.id,
        ...data,
      });
    }
  };

  useEffect(() => {
    form.reset(resetValues);
  }, [transcriptionLanguages, userData, form, resetValues]);

  return (
    <div className="space-y-8">
      <FormProvider {...form}>
        <TranscriptionDefaultSelects
          data={{ transcriptionLanguages, formats, styles }}
          isSubmitting={loading}
          loading={loadingTranscriptionLanguages}
          onChange={handleChange}
        />
      </FormProvider>
    </div>
  );
};

export default TranscriptionSettings;
